import { highLevelPageBreadcrumb } from 'Src/helpers/tournament/highLevelNavigation';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { PermissionSelector } from '../PermissionSelector/PermissionSelector';
import { AppUser } from 'Src/views/App/App';
import { Role } from '../../models/roles';
import { Permission } from '../../models/permission';
import { BurgerButton } from 'Src/components/BurgerButton/BurgerButton';
import { HelpIconButton } from 'Src/components/HelpIconButton/HelpIconButton';
import { SQUADY_ICON_SRC } from 'Src/components/PageHelp/squadyIcon';
import { KIND } from 'Src/consts/school';
import { ROLE } from 'Src/consts/user';
import {
  PAGE_HELP_OPEN_EVENT,
  PAGE_HELP_REGISTER_EVENT,
  PAGE_HELP_UNREGISTER_EVENT
} from 'Src/helpers/pageHelp/pageHelp';
import './TopNavigation.scss';

interface TopbarWizardStep {
  label: string;
  state: 'done' | 'active' | 'pending';
}

interface TopbarWizardState {
  steps: TopbarWizardStep[];
}

interface Props {
  user: AppUser;
  onLogoutClick: (event) => void;
  onRoleChange: (
    roleName: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    permission: Permission
  ) => any;
  history: any;
  availableRoles: Role[];
  onSidebarSwitch?: () => void;
}

function getPageTitle(history: any): string {
  const pathname = history && history.location && history.location.pathname ? history.location.pathname : '/dashboard';
  const lastSegment = pathname
    .split('/')
    .filter(Boolean)
    .pop();

  if (pathname.indexOf('/generalMessages/create') === 0) return 'New message';
  if (pathname === '/generalMessages') return 'General messages';
  if (pathname.indexOf('/school/terms') === 0) return 'Term dates';
  if (pathname.indexOf('/awardTypes') === 0) return 'Award types';
  if (pathname.indexOf('/activities') === 0) return 'Activities & Sports';
  if (pathname.indexOf('/schoolGallery') === 0) return 'School gallery';
  if (pathname.indexOf('/unionGallery') === 0) return 'Union gallery';
  if (pathname.indexOf('/template/forms') === 0 || pathname.indexOf('/template/tournamentRequests') === 0) {
    return 'Form builder';
  }
  if (pathname.indexOf('/school/settings/base/edit') === 0) return 'Edit profile';
  if (pathname.indexOf('/school/settings/public-site') === 0) return 'Public site';
  if (pathname.indexOf('/school/settings/advanced') === 0) return 'Advanced';
  if (pathname.indexOf('/school/settings/gm-email') === 0) return 'General Messages';
  if (pathname.indexOf('/school/settings/base') === 0 || pathname === '/school/settings') return 'Profile';
  if (pathname.indexOf('/school/structure/houses') === 0) return 'Houses';
  if (pathname.indexOf('/school/structure/forms') === 0 || pathname === '/school/structure') {
    return 'Registration forms';
  }
  if (pathname.indexOf('/requests/archive') === 0) return 'Archive';
  if (pathname === '/requests') return 'Inbox';

  if (!lastSegment || lastSegment === 'dashboard') return 'Dashboard';

  return lastSegment
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getPageBreadcrumb(history: any, user: AppUser): string | null {
  const pathname = history && history.location && history.location.pathname ? history.location.pathname : '/dashboard';
  const highLevelTrail = highLevelPageBreadcrumb(pathname, history?.location?.search || '');
  if (highLevelTrail) return highLevelTrail;

  const isParentOrStudent = user.activeRole === ROLE.PARENT || user.activeRole === ROLE.STUDENT;

  if (isParentOrStudent) {
    if (pathname === '/home') return user.activeRole === ROLE.STUDENT ? 'Today' : 'Home';
    if (pathname === '/calendar') return 'Calendar';
    if (pathname === '/children') return 'Children';
    if (pathname === '/basket') return 'Basket';
    if (pathname === '/awards/current') return 'Awards & House points';
    if (pathname === '/myTeams') return 'My teams';
    if (pathname === '/childrenTeams') return 'Children’s teams';
    if (pathname === '/clubs') return 'Clubs';
    if (pathname === '/consentRequests/inbox') return 'Consent requests / Inbox';
    if (pathname === '/consentRequests/history') return 'Consent requests / Past current year';
    if (pathname === '/consentRequests/archive') return 'Consent requests / Past previous years';
    if (pathname === '/clubBookings/inbox') return 'Clubs / Invitations';
    if (pathname === '/clubBookings/history') return 'Clubs / Past current year';
    if (pathname === '/clubBookings/archive') return 'Clubs / Past previous years';
    if (pathname === '/clubProgrammes') return 'Clubs / Browse & book';
    if (/^\/clubProgrammes\/[^/]+\/booking\/?$/.test(pathname)) return 'Clubs / Booking';
    if (pathname === '/availabilityReports/outbox') return 'Availability reports / Current';
    if (pathname === '/availabilityReports/history') return 'Availability reports / Past current year';
    if (pathname === '/availabilityReports/archive') return 'Availability reports / Past previous years';
    if (pathname === '/absenceRequests/outbox') return 'Absence requests / Current';
    if (pathname === '/absenceRequests/archive') return 'Absence requests / Archive';
    if (pathname === '/profile/summary') return 'Profile / Summary';
    if (pathname === '/profile/roles') return 'Profile / Roles';
    if (pathname === '/profile/requests') return 'Profile / Requests';
    if (pathname === '/profile/changePassword') return 'Profile / Change password';
    if (pathname === '/profile/notifications') return 'Profile / Notifications';
    if (pathname === '/profile/subscriptions') return 'Profile / Subscriptions';
    if (pathname === '/profile/verification') return 'Profile / Verification';
  }

  if (pathname === '/generalMessages') return 'Communications / General messages';
  if (pathname === '/eventCommunications') return 'Communications / Chats';
  if (pathname.indexOf('/news') === 0) return 'Communications / News';
  if (pathname.indexOf('/notificationSettings') === 0) return 'Admin console / Notification settings';
  if (pathname.indexOf('/school/terms') === 0) return 'Admin console / Term dates';
  if (pathname.indexOf('/awardTypes') === 0) return 'Admin console / Award types';
  if (pathname === '/awards' || pathname.indexOf('/currentAwards') === 0) return 'Awards';
  if (pathname.indexOf('/houseRace') === 0) return 'Awards / House race';
  if (pathname.indexOf('/tournaments/chats') === 0) return 'Tournaments / Chats';
  if (pathname.indexOf('/tournaments/events') === 0) return 'Tournaments / Events';
  if (pathname.indexOf('/tournaments/teams') === 0) return 'Tournaments / Teams';
  if (pathname.indexOf('/tournaments/teamPlayers') === 0) return 'Tournaments / Team players';
  if (pathname.indexOf('/tournaments/waitingList') === 0) return 'Tournaments / Waiting list';
  if (pathname.indexOf('/tournaments/invites') === 0) return 'Tournaments / Invites';
  if (pathname.indexOf('/tournaments/requests') === 0) return 'Tournaments / Requests';
  if (pathname.indexOf('/tournaments/archive') === 0) return 'Tournaments / Archive';
  if (pathname.indexOf('/tournaments') === 0) return 'Tournaments / Upcoming';
  if (pathname.indexOf('/activities') === 0) return 'Admin console / Activities & Sports';
  if (pathname.indexOf('/tournamentTags') === 0) return 'Admin console / Tournament tags';
  if (pathname.indexOf('/records') === 0) {
    return user.activeSchool && user.activeSchool.kind === KIND.SCHOOL_UNION
      ? 'Admin console / Records'
      : 'Sports & Fixtures / Records';
  }
  if (pathname.indexOf('/schoolGallery') === 0) return 'Admin console / School gallery';
  if (pathname.indexOf('/unionGallery') === 0) return 'Admin console / Union gallery';
  if (pathname.indexOf('/template/forms') === 0 || pathname.indexOf('/template/tournamentRequests') === 0) {
    return 'Admin console / Form builder';
  }
  if (pathname.indexOf('/school/settings') === 0) return 'Admin console / School settings';
  if (pathname.indexOf('/school/structure/houses') === 0) return 'Admin console / School structure / Houses';
  if (pathname.indexOf('/school/structure/forms') === 0 || pathname === '/school/structure') {
    return 'Admin console / School structure / Reg forms';
  }
  if (pathname === '/users') return 'People / All users and contacts';
  if (pathname === '/staff') return 'People / Staff';
  if (pathname === '/students') return 'People / Students';
  if (pathname === '/governors') return 'People / Trustees & Governors';
  if (pathname === '/tags') return 'People / User tags';
  if (pathname.indexOf('/requests/archive') === 0) return 'People / Role requests / Archive';
  if (pathname.indexOf('/requests') === 0) return 'People / Role requests / Inbox';
  if (pathname.indexOf('/messages/inbox') === 0) return 'Messages / Inbox';
  if (pathname.indexOf('/messages/outbox') === 0) return 'Messages / Outbox';
  if (pathname.indexOf('/messages/archive') === 0) return 'Messages / Archive';
  if (pathname.indexOf('/teams') === 0) return 'Sports & Fixtures / Teams';
  if (pathname.indexOf('/events') === 0) return 'Sports & Fixtures / All events';
  if (pathname.indexOf('/venues') === 0) {
    return user.activeSchool && user.activeSchool.kind === KIND.SCHOOL_UNION
      ? 'Admin console / Venues'
      : 'Sports & Fixtures / Venues';
  }
  if (pathname.indexOf('/invites/inbox') === 0) return 'Sports & Fixtures / Invites / Inbox';
  if (pathname.indexOf('/invites/outbox') === 0) return 'Sports & Fixtures / Invites / Outbox';
  if (pathname.indexOf('/invites/archive') === 0) return 'Sports & Fixtures / Invites / Archive';
  if (pathname.indexOf('/leagues/events') === 0) return 'Sports & Fixtures / School leagues / Manage events';
  if (pathname.indexOf('/leagues/news') === 0) return 'Sports & Fixtures / School leagues / Manage news';
  if (pathname.indexOf('/leagues') === 0) return 'Sports & Fixtures / School leagues';
  if (pathname.indexOf('/kit-lab') === 0) return 'Sports & Fixtures / Kit lab';
  if (pathname.indexOf('/invitations') === 0) return 'Clubs / Invitations';
  if (pathname.indexOf('/clubs/edit-participants') === 0) return 'Clubs / Clubs / Edit participants';
  if (pathname.indexOf('/clubs') === 0) return 'Clubs / Clubs';
  if (/^\/clubProgrammes\/[^/]+\/manageclubs\/[^/]+\/fields\/?$/.test(pathname)) {
    return 'Clubs / Activity programmes / Manage clubs / Club booking form';
  }
  if (/^\/clubProgrammes\/[^/]+\/manageclubs\/?$/.test(pathname)) return 'Clubs / Activity programmes / Manage clubs';
  if (/^\/clubProgrammes\/[^/]+\/bookings\/?$/.test(pathname)) return 'Clubs / Activity programmes / Booking manager';
  if (/^\/clubProgrammes\/[^/]+\/fields\/?$/.test(pathname)) return 'Clubs / Activity programmes / Manage form fields';
  if (/^\/clubProgrammes\/[^/]+\/clubs\/?$/.test(pathname)) return 'Clubs / Activity programmes / Add clubs';
  if (pathname.indexOf('/clubProgrammes') === 0) return 'Clubs / Activity programmes';

  return null;
}
export const TopNavigation: React.FunctionComponent<Props> = props => {
  const { user, history, onSidebarSwitch } = props;
  const isEventCreate = history && history.location && history.location.pathname === '/events/manager';
  const [wizardState, setWizardState] = useState<TopbarWizardState | null>(
    isEventCreate
      ? {
          steps: [
            { label: 'Event details', state: 'active' },
            { label: 'Participants', state: 'pending' }
          ]
        }
      : null
  );
  const [breadcrumbOverride, setBreadcrumbOverride] = useState<string | null>(isEventCreate ? 'New event' : null);
  const [pageHelpSources, setPageHelpSources] = useState<{
    [source: string]: { label: string; text?: string };
  }>({});
  const pageHelpSource = pageHelpSources.squady
    ? 'squady'
    : pageHelpSources.legacy
    ? 'legacy'
    : pageHelpSources['squady-fallback']
    ? 'squady-fallback'
    : null;
  const pageHelp = pageHelpSource ? pageHelpSources[pageHelpSource] : null;
  const parentSchoolIds = new Set<string>();
  (props.availableRoles || [])
    .filter(role => role.name === ROLE.PARENT)
    .forEach(role => (role.permissions || []).forEach(permission => {
      if ((permission as any).studentId && permission.schoolId) parentSchoolIds.add(String(permission.schoolId));
    }));
  const isMultiSchoolParent = user.activeRole === ROLE.PARENT && parentSchoolIds.size > 1;
  const schoolName = isMultiSchoolParent
    ? 'Parent account'
    : (user.activeSchool && user.activeSchool.name) || user.activeSchoolName || 'School';
  const pageTitle = getPageTitle(history);
  const pageBreadcrumb = getPageBreadcrumb(history, user);
  useEffect(() => {
    const onWizardChange = (event: Event) => {
      setWizardState((event as CustomEvent<TopbarWizardState>).detail || null);
    };
    const onWizardClear = () => setWizardState(null);

    window.addEventListener('sit:general-message-wizard-header', onWizardChange);
    window.addEventListener('sit:general-message-wizard-header-clear', onWizardClear);
    window.addEventListener('sit:topbar-wizard', onWizardChange);
    window.addEventListener('sit:topbar-wizard-clear', onWizardClear);

    return () => {
      window.removeEventListener('sit:general-message-wizard-header', onWizardChange);
      window.removeEventListener('sit:general-message-wizard-header-clear', onWizardClear);
      window.removeEventListener('sit:topbar-wizard', onWizardChange);
      window.removeEventListener('sit:topbar-wizard-clear', onWizardClear);
    };
  }, []);

  useEffect(() => {
    const onRegister = (event: Event) => {
      const detail = (event as CustomEvent<{ label?: string; text?: string; source?: string }>).detail;
      const source = (detail && detail.source) || 'legacy';
      setPageHelpSources(current => ({
        ...current,
        [source]: {
          label: (detail && detail.label) || 'How this page works',
          text: detail && detail.text
        }
      }));
    };
    const onUnregister = (event: Event) => {
      const detail = (event as CustomEvent<{ source?: string }>).detail;
      const source = (detail && detail.source) || 'legacy';
      setPageHelpSources(current => {
        if (!current[source]) return current;
        const next = { ...current };
        delete next[source];
        return next;
      });
    };
    window.addEventListener(PAGE_HELP_REGISTER_EVENT, onRegister);
    window.addEventListener(PAGE_HELP_UNREGISTER_EVENT, onUnregister);
    return () => {
      window.removeEventListener(PAGE_HELP_REGISTER_EVENT, onRegister);
      window.removeEventListener(PAGE_HELP_UNREGISTER_EVENT, onUnregister);
    };
  }, []);

  useEffect(() => {
    const onBreadcrumbChange = (event: Event) => setBreadcrumbOverride((event as CustomEvent<string>).detail || null);
    const onBreadcrumbClear = () => setBreadcrumbOverride(null);
    window.addEventListener('sit:topbar-breadcrumb', onBreadcrumbChange);
    window.addEventListener('sit:topbar-breadcrumb-clear', onBreadcrumbClear);
    return () => {
      window.removeEventListener('sit:topbar-breadcrumb', onBreadcrumbChange);
      window.removeEventListener('sit:topbar-breadcrumb-clear', onBreadcrumbClear);
    };
  }, []);

  const onMenuKeyDown = event => {
    if (!onSidebarSwitch) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSidebarSwitch();
    }
  };

  return (
    <nav className="sitTopbar">
      <div className="sitTopbar-left">
        {onSidebarSwitch && (
          <div
            className="sitTopbar-menuButton"
            role="button"
            tabIndex={0}
            aria-label="Toggle navigation"
            onClick={onSidebarSwitch}
            onKeyDown={onMenuKeyDown}
          >
            <BurgerButton onClick={() => undefined} />
          </div>
        )}

        <a className="sitTopbar-brand" href="" onClick={event => event.preventDefault()}>
          <img src="/dist/images/logo.svg" alt="Squad In Touch" />
        </a>

        <div className={wizardState ? 'sitTopbar-context sitTopbar-context--wizard' : 'sitTopbar-context'}>
          <div className="sitTopbar-school">{schoolName}</div>
          <div className="sitTopbar-pageRow">
            <div className={breadcrumbOverride || pageBreadcrumb ? 'sitTopbar-breadcrumb' : 'sitTopbar-page'}>
              {breadcrumbOverride || pageBreadcrumb || pageTitle}
            </div>
            {wizardState && wizardState.steps.length > 0 && (
              <div className="sitTopbarWizard" aria-label="Creation progress">
                {wizardState.steps.map((step, index) => (
                  <div key={`${step.label}_${index}`} className="sitTopbarWizard__step" data-state={step.state}>
                    <span className="sitTopbarWizard__dot">
                      {step.state === 'done' ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span className="sitTopbarWizard__label">{step.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="sitTopbar-right">
        <div id="sit-topbar-help-slot" className="sitTopbar-helpSlot" />

        {pageHelp && (
          <HelpIconButton
            className="sitTopbar-help"
            onClick={() =>
              window.dispatchEvent(new CustomEvent(PAGE_HELP_OPEN_EVENT, { detail: { source: pageHelpSource } }))
            }
            title={pageHelp.label}
            label={pageHelp.label}
            text={pageHelp.text}
            iconSrc={SQUADY_ICON_SRC}
          />
        )}

        <PermissionSelector {...props} />
      </div>
    </nav>
  );
};

TopNavigation.displayName = 'TopNavigation';
